import { FC, useState } from 'react';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import HideIcon from '@/components/icons/HideIcon';
import ShowIcon from '@/components/icons/ShowIcon';
import CopyIcon from '@/components/icons/CopyIcon';
import TickIcon from '@/components/icons/TickIcon';
import InformationCircle from '@/components/icons/InformationCircle';
import EToast from '@/components/everridge/EToast';
import { TenantFormData } from '@/features/superAdmin/features/tenants/validators/TenantManageValidator';

type Props = {
	control: Control<TenantFormData>;
	setValue: UseFormSetValue<TenantFormData>;
};

const generateApiKey = () => crypto.randomUUID().replace(/-/g, '');

const TenantApiKeyField: FC<Props> = ({ control, setValue }) => {
	const { t } = useTranslation();
	const [isVisible, setIsVisible] = useState(false);
	const [copied, setCopied] = useState(false);
	const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
	const apiKeyValue = useWatch({ control, name: 'apiKey' });

	const handleGenerateConfirm = () => {
		setValue('apiKey', generateApiKey(), { shouldDirty: true, shouldValidate: true });
		setIsVisible(true);
		setGenerateDialogOpen(false);
	};

	const handleCopy = () => {
		if (!apiKeyValue) return;

		void navigator.clipboard.writeText(apiKeyValue);
		setCopied(true);
		setTimeout(() => setCopied(false), 500);
		EToast({
			variant: 'success',
			message: t('main.superAdmin.tenants.form.apiKey.copySuccessMessage'),
			description: t('main.superAdmin.tenants.form.apiKey.copySuccessDescription'),
		});
	};

	return (
		<div className='flex flex-col gap-2'>
			<div className='flex items-center justify-between gap-2'>
				<h4 className='text-sm font-medium'>
					{t('main.superAdmin.tenants.form.apiKey.title')}
				</h4>
				<Button
					type='button'
					variant='outline'
					size='default'
					onClick={() => setGenerateDialogOpen(true)}>
					{t('main.superAdmin.tenants.form.apiKey.generate')}
				</Button>
			</div>
			<p className='text-xs text-muted-foreground'>
				{t('main.superAdmin.tenants.form.apiKey.sectionHint')}
			</p>
			<Controller
				name='apiKey'
				control={control}
				render={({ field: { value, onChange, onBlur }, fieldState }) => (
					<>
						<div className='relative flex items-center'>
							<input
								type={isVisible ? 'text' : 'password'}
								value={value ?? ''}
								onChange={onChange}
								onBlur={onBlur}
								placeholder={t(
									'main.superAdmin.tenants.form.apiKey.placeholder',
								)}
								autoComplete='off'
								className={cn(
									'w-full p-3.5 pr-20 border border-popover-100 rounded-semi-xl text-xs text-popover-800 bg-popover-10 focus:outline-hidden focus:border focus:border-primary-color-1000 placeholder:text-popover-600 transition-colors duration-150',
									fieldState.invalid ? 'border-error-1000' : '',
								)}
							/>
							<div className='absolute right-3 flex items-center gap-1'>
								<button
									type='button'
									className='relative flex size-6 items-center justify-center disabled:opacity-40'
									disabled={!apiKeyValue}
									onClick={handleCopy}
									aria-label={t(
										'main.superAdmin.tenants.form.apiKey.copy',
									)}>
									<CopyIcon
										className={cn(
											'absolute size-4 transition-opacity duration-150',
											copied ? 'opacity-0' : 'opacity-100',
										)}
									/>
									<TickIcon
										className={cn(
											'absolute size-4 transition-opacity duration-150',
											copied ? 'opacity-100' : 'opacity-0',
										)}
									/>
								</button>
								<button
									type='button'
									className='flex size-6 items-center justify-center'
									onClick={() => setIsVisible((current) => !current)}
									aria-label={
										isVisible
											? t('main.superAdmin.tenants.form.apiKey.hide')
											: t('main.superAdmin.tenants.form.apiKey.show')
									}>
									{isVisible ? <ShowIcon /> : <HideIcon />}
								</button>
							</div>
						</div>
						{fieldState.invalid && (
							<div className='flex items-center gap-1.5'>
								<InformationCircle className='size-4 stroke-error-1000' />
								<span className='text-xs text-error-1000'>
									{t(fieldState.error?.message as string)}
								</span>
							</div>
						)}
					</>
				)}
			/>
			<p className='text-xs text-muted-foreground'>
				{t('main.superAdmin.tenants.form.apiKey.generatedHint')}
			</p>

			<Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t('main.superAdmin.tenants.form.apiKey.generateDialogTitle')}
						</DialogTitle>
						<DialogDescription>
							{t(
								'main.superAdmin.tenants.form.apiKey.generateDialogDescription',
							)}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className='flex-col sm:flex-row gap-2 sm:justify-end'>
						<Button
							type='button'
							variant='outline'
							onClick={() => setGenerateDialogOpen(false)}>
							{t('main.superAdmin.tenants.form.apiKey.generateCancel')}
						</Button>
						<Button type='button' onClick={handleGenerateConfirm}>
							{t('main.superAdmin.tenants.form.apiKey.generateConfirm')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default TenantApiKeyField;
